import React, { useRef, useState } from "react";
import { Formik, Form } from "formik";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Input from "src/components/form/Input";
import useUnsavedChanges from "src/hooks/useUnsavedChanges";
import UnsavedChangesDialog from "../../modals/UnsavedChanges/UnsavedChangesDialog";
import * as Yup from "yup";

const validationSchema = Yup.object({
  addressName: Yup.string().required("Location name is required"),
  street: Yup.string().required("Street is required"),
  city: Yup.string().required("City is required"),
  state: Yup.string().required("State is required"),
  zipCode: Yup.string().required("Zip code is required"),
});

export default function AddressModal({ open, onClose, address = {}, onSave, isNew = false, locationLabel = "Location Name", showSaveToAddressBook = false }) {
  const [saveToBook, setSaveToBook] = useState(false);
  const formikRef = useRef(null);

  const {
    attemptClose,
    confirmDiscard,
    cancelDiscard,
    confirmSave,
    showDialog,
    saving,
  } = useUnsavedChanges({
    onClose,
    isDirtyFn: () => !!formikRef.current?.dirty,
    onSave: async () => {
      await formikRef.current?.submitForm();
    },
  });

  const handleSubmit = (values, formikBag) => {
    return onSave(values, formikBag, saveToBook);
  };

  return (
    <>
    <Dialog open={open} onClose={attemptClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ position: "relative", pb: 0, textAlign: "center" }}>
        <Typography fontSize={24} fontWeight={500} color="text.secondary">
          {isNew ? "Add Address" : "Edit Address"}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={attemptClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Formik
          innerRef={formikRef}
          enableReinitialize
          initialValues={address}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          {({ isSubmitting }) => (
            <Form>
              {showSaveToAddressBook && (
                <Box
                  mt={2}
                  mb={1}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    px: 2,
                    py: 0.5,
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={saveToBook}
                        onChange={e => setSaveToBook(e.target.checked)}
                      />
                    }
                    label="Save to Address Book"
                    sx={{ mr: 0 }}
                  />
                </Box>
              )}
              <Box pt={2} pb={3}>
                <Stack spacing={2}>
                  <Input fullWidth label={locationLabel} name="addressName" showNAButton={false} />
                  <Input fullWidth label="Street" name="street" showNAButton={false} />
                  <Input fullWidth label="City" name="city" showNAButton={false} />
                  <Input fullWidth label="State" name="state" showNAButton={false} />
                  <Input fullWidth label="Zip Code" name="zipCode" showNAButton={false} />
                </Stack>
              </Box>
              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  size="large"
                  startIcon={isSubmitting && <CircularProgress size={20} />}
                  disabled={isSubmitting}
                >
                  Save
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
    {showDialog && (
      <UnsavedChangesDialog
        open={showDialog}
        onCancel={cancelDiscard}
        onDiscard={confirmDiscard}
        onSave={confirmSave}
        saving={saving}
      />
    )}
    </>
  );
}
